import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as PricingAPI from 'api/Pricing';
import { dispatch } from 'store';
import { TYPE_OF_FIELD_IN_COLUMN } from 'utils/scorecard';
import { openSnackbar } from 'store/reducers/snackbar';

const displayMessage = ({ type, msg }) => {
  dispatch(
    openSnackbar({
      open: true,
      message: msg ? msg : type === 'success' ? 'Success' : 'Something went wrong',
      variant: 'alert',
      alert: {
        color: type || 'success'
      },
      close: false
    })
  );
};

const initialState = {
  pricing: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: 'Stripe ID', key: 'djstripe_id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'ID', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Active', key: 'active', field_type: TYPE_OF_FIELD_IN_COLUMN.BOOLEAN, width: 120 },
      { title: 'Unit Amount', key: 'unit_amount', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Type', key: 'type', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Currency', key: 'currency', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Live Mode', key: 'livemode', field_type: TYPE_OF_FIELD_IN_COLUMN.BOOLEAN, width: 120 }
    ],
    data: null
  },
  subscription: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Slug', key: 'slug', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Stripe Price', key: 'stripe_price.name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Interval', key: 'interval', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Strip Mode', key: 'stripe_mode', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Priority', key: 'priority', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Is_Active', key: 'is_active', field_type: TYPE_OF_FIELD_IN_COLUMN.BOOLEAN, width: 120 },
      { title: 'Credits Minutes', key: 'credits_minutes', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'No Of Agents', key: 'no_of_agents', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Default', key: 'default', field_type: TYPE_OF_FIELD_IN_COLUMN.BOOLEAN, width: 120 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null,
    allPricings: null
  },
  subscriptionType: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Plan Type', key: 'plan_type', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Subscription Plans', key: 'subscription_plans', field_type: TYPE_OF_FIELD_IN_COLUMN.ARRAY, width: 120 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null,
    allSubscriptions: null
  },
  allSubscriptionPlans: null,
  status: '',
  ui: {
    loading: false,
    msg: ''
  }
};

const getPricingData = createAsyncThunk('pricing/getPricingData', async ({ params, signal }) => {
  const res = await PricingAPI.getPricingData(params, signal);
  return res?.data?.data;
});

//Subscription Tab
const getSubscriptionData = createAsyncThunk('pricing/getSubscriptionData', async ({ params, signal }) => {
  const res = await PricingAPI.getSubscriptionData(params, signal);
  return res?.data?.data;
});

const createSubscription = createAsyncThunk('pricing/createSubscription', async (params) => {
  try {
    const res = await PricingAPI.createSubscription(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateSubscription = createAsyncThunk('pricing/updateSubscription', async (params) => {
  try {
    const res = await PricingAPI.updateSubscription(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteSubscription = createAsyncThunk('pricing/deleteSubscription', async (id) => {
  try {
    const res = await PricingAPI.deleteSubscription(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const getAllPricings = createAsyncThunk('pricing/getAllPricings', async () => {
  const res = await PricingAPI.getAllPricings();
  return res?.data?.data;
});

//Subscription Type Tab
const getSubscriptionTypeData = createAsyncThunk('pricing/getSubscriptionTypeData', async ({ params, signal }) => {
  const res = await PricingAPI.getSubscriptionTypeData(params, signal);
  return res?.data?.data;
});

const createSubscriptionType = createAsyncThunk('pricing/createSubscriptionType', async (params) => {
  try {
    const res = await PricingAPI.createSubscriptionType(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateSubscriptionType = createAsyncThunk('pricing/updateSubscriptionType', async (params) => {
  try {
    const res = await PricingAPI.updateSubscriptionType(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteSubscriptionType = createAsyncThunk('pricing/deleteSubscriptionType', async (id) => {
  try {
    const res = await PricingAPI.deleteSubscriptionType(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const getAllSubscriptions = createAsyncThunk('pricing/getAllSubscriptions', async () => {
  const res = await PricingAPI.getAllSubscriptions();
  return res?.data?.data;
});

const getAllSubscriptionPlans = createAsyncThunk('pricing/getAllSubscriptionPlans', async () => {
  const res = await PricingAPI.getAllSubscriptionPlans();
  return res?.data?.data;
});

const PricingSlice = createSlice({
  name: 'pricing',
  initialState: initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },

    //Pricing Tab
    setPricingCurrentPage: (state, action) => {
      state.pricing.pagination.currentPage = action.payload;
    },
    setPricingPageSize: (state, action) => {
      state.pricing.pagination.pageSize = action.payload;
    },

    //Subscription Tab
    setSubscriptionCurrentPage: (state, action) => {
      state.subscription.pagination.currentPage = action.payload;
    },
    setSubscriptionPageSize: (state, action) => {
      state.subscription.pagination.pageSize = action.payload;
    },

    //Subscription Type Tab
    setSubscriptionTypeCurrentPage: (state, action) => {
      state.subscriptionType.pagination.currentPage = action.payload;
    },
    setSubscriptionTypePageSize: (state, action) => {
      state.subscriptionType.pagination.pageSize = action.payload;
    }
  },
  extraReducers: {
    [getPricingData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getPricingData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.pricing.pagination.totalRecords = action.payload?.count;
      state.pricing.data = action.payload?.results || [];
    },
    [getPricingData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },

    //Subscription Tab
    [getAllPricings.fulfilled]: (state, action) => {
      state.subscription.allPricings = action.payload;
    },
    [getSubscriptionData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getSubscriptionData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.subscription.pagination.totalRecords = action.payload?.count;
      state.subscription.data = action.payload?.results || [];
    },
    [getSubscriptionData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createSubscription.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [createSubscription.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [createSubscription.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },
    [updateSubscription.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [updateSubscription.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [updateSubscription.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },
    [deleteSubscription.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [deleteSubscription.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [deleteSubscription.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    //Subscription Type Tab
    [getAllSubscriptions.fulfilled]: (state, action) => {
      state.subscriptionType.allSubscriptions = action.payload;
    },
    [getSubscriptionTypeData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getSubscriptionTypeData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.subscriptionType.pagination.totalRecords = action.payload?.count;
      state.subscriptionType.data = action.payload?.results || [];
    },
    [getSubscriptionTypeData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createSubscriptionType.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [createSubscriptionType.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [createSubscriptionType.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },
    [updateSubscriptionType.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [updateSubscriptionType.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [updateSubscriptionType.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },
    [deleteSubscriptionType.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [deleteSubscriptionType.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [deleteSubscriptionType.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [getAllSubscriptionPlans.fulfilled]: (state, action) => {
      state.allSubscriptionPlans = action.payload;
    }
  }
});

const {
  resetState,
  setPricingCurrentPage,
  setPricingPageSize,
  setSubscriptionCurrentPage,
  setSubscriptionPageSize,
  setSubscriptionTypePageSize,
  setSubscriptionTypeCurrentPage
} = PricingSlice.actions;

export default PricingSlice.reducer;

export {
  resetState,
  getAllSubscriptionPlans,
  //Pricing tab
  setPricingCurrentPage,
  setPricingPageSize,
  getPricingData,
  //Subscription Tab
  getAllPricings,
  setSubscriptionCurrentPage,
  setSubscriptionPageSize,
  getSubscriptionData,
  createSubscription,
  updateSubscription,
  deleteSubscription,

  //Subscription Type Tab
  getAllSubscriptions,
  setSubscriptionTypeCurrentPage,
  setSubscriptionTypePageSize,
  getSubscriptionTypeData,
  createSubscriptionType,
  updateSubscriptionType,
  deleteSubscriptionType
};
